import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import * as SentryAngular from '@sentry/angular';
import * as SentryCapacitor from '@sentry/capacitor';
import {STATIC_CONFIGS} from '../../../configs/static.config';
import {environment} from '../../../environments/environment';
import {SENTRY_CAPTURE_EXCEPTION, SENTRY_SET_TAG, SENTRY_SET_TAGS} from '../../constants/sentry.constants';
import {SystemInfo} from '../../models/system-info.model';
import {isUnauthorizedHttpError} from '../../util/is-unauthorized-http-error.util';

@Injectable({
    providedIn: 'root',
})
export class SentryService {
    private readonly captureExceptionFn: typeof SentryAngular.captureException = inject(SENTRY_CAPTURE_EXCEPTION);
    private readonly setTagFn: typeof SentryAngular.setTag = inject(SENTRY_SET_TAG);
    private readonly setTagsFn: typeof SentryAngular.setTags = inject(SENTRY_SET_TAGS);

    /**
     * Captures the given error and reports it to Sentry.
     * In non-production environments the error is additionally logged to the console.
     * {@link HttpErrorResponse} instances are converted into a readable error message and
     * enriched with a response context (body, status code and URL).
     *
     * @param {unknown} error The error to capture and report.
     * @param {string} message Optional custom message that is prepended to the message of the error.
     */
    public captureException(error: unknown, message?: string): void {
        if (isUnauthorizedHttpError(error)) {
            return;
        }
        if (!environment.production) {
            const logArgs = message ? [
                message,
                error,
            ] : [error];
            console.error('SentryService.captureException', ...logArgs);
        }
        if (error instanceof HttpErrorResponse) {
            const httpMessage = `HTTP ${error.status}: ${error.url ?? 'unknown URL'}`;
            this.captureExceptionFn(new Error(message ? `${message} Error: ${httpMessage}` : httpMessage), {
                contexts: {
                    response: {
                        body: JSON.stringify(error.error),
                        status_code: error.status,
                        url: error.url,
                    },
                },
            });
        } else if (message) {
            this.captureExceptionFn(new Error(`${message} Error: ${(error as Error)?.message}`));
        } else {
            this.captureExceptionFn(error);
        }
    }

    /**
     * Initializes the Sentry Capacitor integration with the application's configuration.
     * Does nothing if Sentry is disabled for the current environment or the app is running
     * against a local "192." host.
     */
    public initSentryCapacitor(): void {
        if (!environment.sentry || window.location.href.includes('192.')) {
            return;
        }
        SentryCapacitor.init({
            dsn: STATIC_CONFIGS.sentry.dsn,
            integrations: [
                SentryAngular.browserTracingIntegration(),
            ],
            tracePropagationTargets: STATIC_CONFIGS.sentry.tracePropagationTargets,
            environment: environment.environmentName,
            tracesSampleRate: 0.5,
            release: environment.version,
            beforeSend: (event: SentryAngular.ErrorEvent, hint: SentryAngular.EventHint) => isUnauthorizedHttpError(hint.originalException) ? null : event,
        }, SentryAngular.init);
    }

    /**
     * Sets a single Sentry tag identified by the given key.
     *
     * @param {string} key The key of the tag to set.
     * @param {Parameters<typeof SentryAngular.setTag>[1]} value The value of the tag to set.
     */
    public setTag(key: string, value: Parameters<typeof SentryAngular.setTag>[1]): void {
        this.setTagFn(key, value);
    }

    /**
     * Sets multiple Sentry tags at once from the given key-value map.
     *
     * @param {Parameters<typeof SentryAngular.setTags>[0]} tags The map of tags to set.
     */
    public setTags(tags: Parameters<typeof SentryAngular.setTags>[0]): void {
        this.setTagsFn(tags);
    }

    /**
     * Sets the Sentry tags derived from the given system information.
     * A missing screen resolution is reported as "unknown".
     *
     * @param {SystemInfo} systemInfo The system information used to build the tags.
     */
    public setSystemInfoTags(systemInfo: SystemInfo): void {
        this.setTags({
            app_type: systemInfo.appType,
            app_version: systemInfo.appVersion,
            app_browser: systemInfo.browser,
            app_browser_version: systemInfo.browserVersion,
            app_device: systemInfo.device,
            app_os_version: systemInfo.osVersion,
            app_os: systemInfo.os,
            app_screen_resolution: systemInfo.screenResolution ?? 'unknown',
        });
    }
}
